import React, { useRef, useEffect, useState } from 'react';

// The poster paints instantly (LCP) while a lightweight self-hosted H.264 clip
// autoplays on top the moment it can — on desktop AND mobile. Muted + playsInline
// keeps mobile autoplay allowed; if a browser blocks autoplay (e.g. iOS low-power
// mode) the poster simply stays visible.
const VideoBackground = ({ videoUrl, posterUrl, className = '', startTime = 0, children }) => {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let revealed = false;
    let sought = false;
    const reveal = () => { if (!revealed) { revealed = true; setVideoReady(true); } };
    const onReady = () => {
      if (!sought && startTime > 0 && Number.isFinite(video.duration) && video.duration > startTime) {
        sought = true;
        try { video.currentTime = startTime; } catch (e) { /* seek not ready */ }
      }
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    };
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('canplay', onReady);
    video.addEventListener('playing', reveal);
    video.addEventListener('timeupdate', reveal);
    // Defer the heavy video download until the browser is idle so it never
    // competes with the LCP poster paint / fonts on slow mobile connections.
    let startId;
    const kickOff = () => { try { video.load(); } catch (e) { /* noop */ } };
    if ('requestIdleCallback' in window) {
      startId = window.requestIdleCallback(kickOff, { timeout: 2500 });
    } else {
      startId = setTimeout(kickOff, 1200);
    }
    const fallback = setTimeout(reveal, 3000);
    return () => {
      clearTimeout(fallback);
      if ('cancelIdleCallback' in window && startId) window.cancelIdleCallback(startId);
      else clearTimeout(startId);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('playing', reveal);
      video.removeEventListener('timeupdate', reveal);
    };
  }, [startTime, videoUrl]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Background media */}
      <div className="absolute inset-0 z-0">
        {/* Poster paints instantly and acts as the LCP element */}
        <img
          src={posterUrl}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
          data-testid="hero-poster"
          fetchpriority="high"
          decoding="async"
        />
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          poster={posterUrl}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
          data-testid="background-video"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-cobra-black/85 via-cobra-black/70 to-cobra-black/90" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default VideoBackground;
